<template>
  <div ref="elementContainer" class="element-container">
    <div
      class="button-wrapper"
      @click="handleOpenClick"
      @keydown.enter="
        (e) => {
          handleOpenClick(e)
          e.stopPropagation()
        }
      "
    >
      <slot name="button"> </slot>
    </div>
    <div v-if="show" ref="actionMenuRef" class="action-menu" :style="menuStyle" @click.stop>
      <template v-for="action in actions" :key="action.actionName">
        <div
          v-if="!item || (item && (action.appliesOn?.includes(item.type) ?? true))"
          class="action-item"
        >
          <ActionButton
            :action="action"
            show-text
            size="fill"
            stop-click-propagation
            @action="handleActionClick"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch, CSSProperties, nextTick } from 'vue'
import { Action, DirectoryItem } from './types'
import ActionButton from './ActionButton.vue'

type Props = {
  actions: Action[]
  item?: DirectoryItem
  invert?: boolean
  tabIndex?: number
  stopClickPropagation?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  item: undefined,
  invert: false,
  stopClickPropagation: false,
  tabIndex: 0
})

const elementContainer = ref<HTMLElement | undefined>()
const actionMenuRef = ref<HTMLElement | undefined>()
const show = ref(false)
const menuStyle = ref<CSSProperties>({})

type Emits = {
  (e: 'action', action: Action, item?: DirectoryItem): void
  (e: 'close'): void
}
const emits = defineEmits<Emits>()

const handleOpenClick = (e: Event) => {
  show.value = !show.value
  if (props.stopClickPropagation) {
    e.stopPropagation()
  }
}

const handleActionClick = (action: Action) => {
  emits('action', action, props.item)
  show.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (
    show.value &&
    elementContainer.value &&
    !elementContainer.value.contains(event.target as Node) &&
    actionMenuRef.value &&
    !actionMenuRef.value.contains(event.target as Node)
  ) {
    show.value = false
  }
}

watch(show, async (newValue) => {
  if (newValue) {
    await nextTick()

    if (elementContainer.value && actionMenuRef.value) {
      const triggerRect = elementContainer.value.getBoundingClientRect()
      const menuEl = actionMenuRef.value
      const menuHeight = menuEl.offsetHeight
      const menuWidth = menuEl.offsetWidth

      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const viewportMargin = 8

      let finalTop: number
      let finalLeft: number

      finalTop = triggerRect.bottom + viewportMargin / 2

      if (finalTop + menuHeight > viewportHeight - viewportMargin) {
        const topIfAbove = triggerRect.top - menuHeight - viewportMargin / 2

        if (topIfAbove >= viewportMargin) {
          finalTop = topIfAbove
        } else {
          finalTop = viewportHeight - menuHeight - viewportMargin
          if (finalTop < viewportMargin) {
            finalTop = viewportMargin
          }
        }
      }
      finalTop = Math.max(
        viewportMargin,
        Math.min(finalTop, viewportHeight - menuHeight - viewportMargin)
      )

      finalLeft = triggerRect.left - menuWidth

      if (finalLeft < viewportMargin) {
        const alternativeLeft = triggerRect.left
        if (
          alternativeLeft + menuWidth <= viewportWidth - viewportMargin &&
          alternativeLeft >= viewportMargin
        ) {
          finalLeft = alternativeLeft
        } else {
          finalLeft = viewportMargin
        }
      } else if (finalLeft + menuWidth > viewportWidth - viewportMargin) {
        const alternativeLeftAlignRight = triggerRect.right - menuWidth
        if (
          alternativeLeftAlignRight >= viewportMargin &&
          alternativeLeftAlignRight + menuWidth <= viewportWidth - viewportMargin
        ) {
          finalLeft = alternativeLeftAlignRight
        } else {
          finalLeft = viewportWidth - menuWidth - viewportMargin
        }
      }

      finalLeft = Math.max(
        viewportMargin,
        Math.min(finalLeft, viewportWidth - menuWidth - viewportMargin)
      )
      if (menuWidth > viewportWidth - 2 * viewportMargin) {
        finalLeft = viewportMargin
      }

      menuStyle.value = {
        top: `${finalTop}px`,
        left: `${finalLeft}px`
      }
    }
    document.addEventListener('click', handleClickOutside, true)
  } else {
    document.removeEventListener('click', handleClickOutside, true)
    emits('close')
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.element-container {
  position: relative;
  display: inherit;
}

.action-menu {
  position: fixed;
  display: flex;
  width: 140px;
  padding: 8px 0px;
  flex-direction: column;
  align-items: flex-start;
  z-index: variables.$z-index-overlay;
  border-radius: 12px;
  border: 1px solid var(--Greyscale-Secondary-Grey, #767676);
  background: var(--Greyscale-Pure-White, #fff);

  /* Elevation 3 */
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);

  .action-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
    align-self: stretch;

    &:hover {
      background-color: #3f46d6;
      color: #fff;
      :deep(.button-base .text) {
        color: #fff;
      }
    }

    :deep(.button-base),
    :deep(.tooltip-container) {
      width: 100%;
    }
  }
}
</style>
